import type { ReactNode } from 'react';
import { Badge } from '../../atoms/Badge/Badge';
import styles from './KPIText.module.css';

/**
 * Figma: `_KPI Text` (778:8734) — the figure inside a `KPI` metric.
 *
 * `Text md/Medium` (16/16 at -2%) in `text/primary`, with an optional `Badge`
 * `space/6` to its right, centred on the figure's line box.
 *
 * **The tone axis stopped tinting the figure in the 2026-09-14 update.** It used
 * to be `Neutral | Positive | Negative` painting `text/primary`, `text/success`
 * and `text/error`. `Neutral` is gone, and the two that remain *both* bind
 * `text/primary` — so the figure is always dark and the variant's only job now
 * is to pick the nested `Badge`'s type:
 *
 * | Figma variant | figure | badge |
 * |---|---|---|
 * | `Positive` | `text/primary` | `Badge Type=Positive` — `component/badge/success-surface` |
 * | `Negative` | `text/primary` | `Badge Type=Negative` — `component/badge/error-surface`   |
 *
 * That is why `badge` here is the badge's *content* rather than a built `Badge`
 * node: in Figma the tone and the badge are one decision, and a slot would let a
 * caller pair `tone="negative"` with a green badge, which the design cannot
 * express. Omit `badge` and none renders — Figma's `Badge?#778:4` boolean.
 *
 * With no badge the two tones are indistinguishable, which is a real quirk of the
 * design rather than a gap here: 81 of the file's 84 `_KPI Text` instances are
 * `Positive` with the badge off, and they render identically to `Negative`.
 * Reported in plan.md §7.
 *
 * Underscore-prefixed in Figma, which marks it internal. Exported anyway,
 * following what the file's other `_`-prefixed molecules already do, and because
 * `KPI`'s public `KPIMetric.tone` is typed by `KPITextTone`.
 */
export type KPITextTone = 'positive' | 'negative';

export type KPITextProps = {
  /**
   * Figma `Property 1`. Selects the badge's colour; the figure is `text/primary`
   * either way. Inert when no `badge` is passed.
   */
  tone?: KPITextTone;
  /**
   * Figma's `Badge?` boolean and the text inside the nested `Badge` — the delta,
   * e.g. `+21.76%`. Passing it is the toggle; omit it for a bare figure. The
   * badge's colour comes from `tone`, not from here.
   */
  badge?: ReactNode;
  className?: string;
  /** Figma `Text` — the figure itself. */
  children?: ReactNode;
};

export function KPIText({ tone = 'positive', badge, className, children }: KPITextProps) {
  return (
    <span className={[styles.kpiText, className].filter(Boolean).join(' ')}>
      <span className={styles.value} title={typeof children === 'string' ? children : undefined}>
        {children}
      </span>
      {badge !== undefined && <Badge type={tone}>{badge}</Badge>}
    </span>
  );
}
